import React, { useContext, useRef, useState } from 'react'
import { usePopper } from 'react-popper'

import { Box, Flex, Icon } from '@driftt/dds'
import { faCircleInfo } from '@driftt/dds.icons/light/faCircleInfo'

import FadeInAnimation from 'components/FadeInAnimation'
import TooltipBody from 'components/TooltipBody'
import { CurrentUserContext } from 'context/currentUserContext'
import { useDebouncedCallback } from 'hooks/useDebouncedCallback'
import { segmentTrack, TrackType } from 'utils/tracking'

const ScoreGraphTooltip = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const infoContainerRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  const currentUser = useContext(CurrentUserContext)

  const { styles: popperStyles, attributes } = usePopper(containerRef?.current, popperElement, {
    placement: 'right-end',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [-30, 10]
        }
      },
      {
        name: 'preventOverflow',
        options: {
          rootBoundary: 'document',
          boundary: 'clippingParents',
          mainAxis: true,
          altAxis: false,
          padding: 0,
          altBoundary: true,
          tether: false
        }
      }
    ]
  })

  const debouncedMouseEvent = useDebouncedCallback((toggle: boolean) => {
    if (toggle) {
      segmentTrack(TrackType.SCORE_GRAPH_TOOLTIP_HOVER, {
        userId: currentUser?.id,
        orgId: currentUser?.orgId
      })
    }
    setIsOpen(toggle)
  }, 100)

  return (
    <Box
      onMouseEnter={() => {
        debouncedMouseEvent(true)
      }}
      onMouseLeave={() => debouncedMouseEvent(false)}
      ref={containerRef}
      css={{
        fontSize: '$half',
        position: 'relative',
        display: 'block'
      }}>
      <Flex alignItems="center" ref={infoContainerRef}>
        <Icon
          css={{
            width: '10px',
            height: '10px',
            cursor: 'pointer'
          }}
          icon={faCircleInfo}
        />
        {isOpen && (
          <FadeInAnimation delay={200} inProp={isOpen}>
            <Box
              css={{ zIndex: 100 }}
              ref={setPopperElement}
              style={popperStyles.popper}
              {...attributes.popper}>
              <TooltipBody>How the engagement score has changed in the last 2 weeks</TooltipBody>
            </Box>
          </FadeInAnimation>
        )}
      </Flex>
    </Box>
  )
}

export default ScoreGraphTooltip
